import { Emitter } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ReportAccessStateService } from '../report-access-state-service';

const REPORT_ACCESS_DENIED_ERROR_CODE = 33110004;
const handledErrors = new WeakSet<object>();

type ReportAccessError = {
    cause?: {
        error_code?: number | string;
        error_params?: ReportAccessErrorParams;
        status?: number;
    };
    response?: {
        data?: {
            data?: {
                error_params?: ReportAccessErrorParams;
            };
            error_code?: number | string;
            error_params?: ReportAccessErrorParams;
        };
        status?: number;
    };
    status?: number;
};

type ReportAccessErrorParams = {
    report_owner?: unknown;
};

/**
 * 判断错误是否已经由报告无权限页面接管。
 * @param error 请求错误
 */
export function isReportAccessDeniedHandledError(error: unknown): boolean {
    return typeof error === 'object' && error !== null && handledErrors.has(error);
}

function isReportAccessDeniedError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    const accessError = error as ReportAccessError;
    const errorCode = accessError.cause?.error_code ?? accessError.response?.data?.error_code;
    const httpStatus = accessError.response?.status ?? accessError.cause?.status ?? accessError.status;
    return Number(errorCode) === REPORT_ACCESS_DENIED_ERROR_CODE || httpStatus === 403;
}

function getReportOwner(error: unknown): string {
    if (typeof error !== 'object' || error === null) {
        return '';
    }
    const accessError = error as ReportAccessError;
    const reportOwner = accessError.cause?.error_params?.report_owner
        ?? accessError.response?.data?.error_params?.report_owner
        ?? accessError.response?.data?.data?.error_params?.report_owner;
    return typeof reportOwner === 'string' ? reportOwner.trim() : '';
}

@injectable()
export class ReportAccessStateServiceImpl implements ReportAccessStateService {
    private _denied = false;
    private _reportOwner = '';
    private readonly _onDeniedChangeEmitter = new Emitter<boolean>();
    private readonly _onReportOwnerChangeEmitter = new Emitter<string>();

    readonly onDeniedChange = this._onDeniedChangeEmitter.event;
    readonly onReportOwnerChange = this._onReportOwnerChangeEmitter.event;

    get denied(): boolean {
        return this._denied;
    }

    get reportOwner(): string {
        return this._reportOwner;
    }

    handleError(error: unknown): boolean {
        if (!isReportAccessDeniedError(error)) {
            return false;
        }
        handledErrors.add(error as object);
        const reportOwner = getReportOwner(error);
        if (reportOwner && reportOwner !== this._reportOwner) {
            this._reportOwner = reportOwner;
            this._onReportOwnerChangeEmitter.fire(reportOwner);
        }
        if (!this._denied) {
            this._denied = true;
            this._onDeniedChangeEmitter.fire(true);
        }
        return true;
    }

    restore(): void {
        if (this._reportOwner) {
            this._reportOwner = '';
            this._onReportOwnerChangeEmitter.fire('');
        }
        if (this._denied) {
            this._denied = false;
            this._onDeniedChangeEmitter.fire(false);
        }
    }
}
